import {batch} from 'react-redux';

import {validateMinVolumeAllowed} from 'lib/protocolHelper';
import {
  getMinTipPositionByLabwareId,
  restoreTipCountForSingleCard,
  updateRunningProtocol,
} from './protocolManagementBusiness';
import {saveState} from './stateBusiness';
import * as taskListBusinessAction from './taskListBusinessAction';
import {getNewCardName, generateCardIDAndTaskIDAndNumber} from './commonTaskListBusiness';
import * as cardStateAction from '../actions/cardStateAction';
import * as labwareStateAction from '../actions/labwareStateAction';
import * as temporaryStateAction from '../actions/temporaryStateAction';
import {TaskType, CardType, WellCount, TipChannelType, PipettingParameterFieldKey} from '../../constants/dataEnum';
import {find, filterToArray} from '../../lib/objectHelper';
import {globalAddUndoItem} from './undoRedoBusinessAction';
import {convertRowToNumeric} from '../../contents/ProtocolEditor/VirtualTray/labwareHelper';
import {getRangeMixingSpeedPipettingParameter} from './pipettingParameterBusiness';

const createMixingCard = (cardId) => async (dispatch, getState) => {
  const {cardState} = getState();
  const {cards} = cardState;

  const card = cards[cardId];

  if (card) {
    return;
  }

  const cardName = getNewCardName(cards, CardType.MIXING);

  await dispatch(cardStateAction.addCard(cardId, cardName, CardType.MIXING));
  dispatch(cardStateAction.setCardIsSelected(cardId));
};

const getStartRowColumn = (labwareId, row, column, rowTipCount, columnTipCount) => (dispatch, getState) => {
  const {labwareState} = getState();

  const selectedLabware = find(labwareState, {LabwareID: labwareId});

  let startRow = row;
  let startColumn = column;

  if (selectedLabware) {
    const {rowCount: labwareRowCount, colCount: labwareColCount} = selectedLabware;

    const labwareWellCount = labwareColCount * labwareRowCount;
    const is384 = labwareWellCount === WellCount.WELL_384;

    let skipWellRow = 0;
    let skipWellColumn = 0;

    if (is384) {
      skipWellRow = 1;
      skipWellColumn = 1;
    }

    const rowTipCountNeeded = skipWellRow === 0 ? rowTipCount : rowTipCount + (rowTipCount - 1) * skipWellRow;
    const columnTipCountNeeded =
      skipWellColumn === 0 ? columnTipCount : columnTipCount + (columnTipCount - 1) * skipWellColumn;

    if (startRow + rowTipCountNeeded > labwareRowCount) {
      startRow = labwareRowCount - rowTipCountNeeded + 1;
    }

    if (startColumn + columnTipCountNeeded > labwareColCount) {
      startColumn = labwareColCount - columnTipCountNeeded + 1;
    }

    if (is384) {
      if (rowTipCount === 8) {
        startRow = startRow >= 2 ? 2 : 1;
      } else if (columnTipCount === 12) {
        startColumn = startColumn >= 2 ? 2 : 1;
      }
    }

    // FIX ME hack for trough
    if (labwareColCount === 1) {
      startColumn = 1;
    }
    if (labwareRowCount === 1) {
      startRow = 1;
    }
  }

  return [startRow, startColumn];
};

export const addMixingTask =
  (
    labwareId = 0,
    row = '',
    column = '',
    rowTipCount = 1,
    columnTipCount = 1,
    initialMixingTask = null,
    volume = 0,
    skipCardCreation = false,
  ) =>
  (dispatch, getState) => {
    const {getWellTarget} = taskListBusinessAction;
    const {cardState} = getState();
    const {cards, tasks} = cardState;
    const selectedCard = find(cards, {isSelected: true});
    const [cardId, taskId, number] = generateCardIDAndTaskIDAndNumber(
      cards,
      tasks,
      CardType.MIXING,
      selectedCard?.ID,
      TaskType.MIXING,
      null,
      {row: rowTipCount, column: columnTipCount},
    );

    let mixingCardID = cardId;
    let mixingTaskID = taskId;
    if (selectedCard && skipCardCreation) {
      mixingCardID = selectedCard.ID;
      mixingTaskID = initialMixingTask.ID ?? mixingTaskID;
    }

    const cardName = cards[mixingCardID]?.name || getNewCardName(cards, CardType.MIXING);

    const [startRow, startColumn] = dispatch(getStartRowColumn(labwareId, row, column, rowTipCount, columnTipCount));

    const minTipPosition = dispatch(getMinTipPositionByLabwareId(labwareId));

    const rangeMixingSpeed = dispatch(getRangeMixingSpeedPipettingParameter({type: TaskType.MIXING}));

    const {validatedVolume: mixingVolume} = dispatch(validateMinVolumeAllowed(volume));

    const task = initialMixingTask
      ? {
          ...initialMixingTask,
          ID: mixingTaskID,
          number,
          labwareID: labwareId,
          row: startRow,
          column: startColumn,
          rowTipCount,
          columnTipCount,
          mixingPositionAspirate: initialMixingTask?.mixingPositionAspirate ?? minTipPosition,
          mixingPositionDispense: initialMixingTask?.mixingPositionDispense ?? minTipPosition,
          enableMixing: initialMixingTask?.enableMixing ?? true,
          mixingCycle: initialMixingTask?.mixingCycle ?? 3,
          mixingVolume,
          volume: mixingVolume,
          mixingPosition: initialMixingTask?.mixingPosition ?? minTipPosition,
          mixingSpeed: initialMixingTask?.mixingSpeed ?? rangeMixingSpeed.default,
          retractSpeed: initialMixingTask?.retractSpeed ?? 20,
          isIgnored: initialMixingTask?.isIgnored ?? false,
        }
      : {
          ID: mixingTaskID,
          number,
          type: TaskType.MIXING,
          labwareID: labwareId,
          row: startRow,
          column: startColumn,
          rowTipCount,
          columnTipCount,
          enableMixing: true,
          mixingCycle: 3,
          mixingVolume,
          volume: mixingVolume,
          mixingPosition: minTipPosition,
          mixingSpeed: rangeMixingSpeed.default,
          retractSpeed: 20,
          isIgnored: false,
          mixingPositionAspirate: minTipPosition,
          mixingPositionDispense: minTipPosition,
        };

    const [alphabetRow, numericColumn] = dispatch(
      getWellTarget(task.labwareID, TipChannelType.TIP_CHANNEL_1, task.row, task.column),
    );

    batch(() => {
      if (!(selectedCard && skipCardCreation)) {
        dispatch(createMixingCard(mixingCardID));
      }
      dispatch(
        cardStateAction.addCardTask(mixingCardID, cardName, CardType.MIXING, mixingTaskID, {
          ...task,
          row: alphabetRow,
          column: numericColumn,
        }),
      );
      dispatch(restoreTipCountForSingleCard(mixingCardID));
    });
  };

export const editMixingTask =
  (labwareId = 0, row = '', column = '', rowTipCount = 1, columnTipCount = 1, volume = 0) =>
  (dispatch, getState) => {
    const {getWellTarget} = taskListBusinessAction;

    const {cardState} = getState();
    const {cards, tasks} = cardState;

    const selectedCard = find(cards, {isSelected: true});

    const [startRow, startColumn] = dispatch(getStartRowColumn(labwareId, row, column, rowTipCount, columnTipCount));

    const allTasks = tasks ?? [];
    const task = filterToArray(allTasks, {ID: selectedCard.tasks})[0];

    if (!task) {
      return;
    }

    const minTipPosition = dispatch(getMinTipPositionByLabwareId(labwareId));

    const {labwareID: oldLabwareId, mixingPosition: oldMixingPosition} = task;

    const mixingPosition = labwareId === oldLabwareId ? oldMixingPosition : minTipPosition;

    const [alphabetRow, numericColumn] = dispatch(
      getWellTarget(task.labwareID, TipChannelType.TIP_CHANNEL_1, startRow, startColumn),
    );

    const {validatedVolume} = dispatch(validateMinVolumeAllowed(volume));

    batch(() => {
      dispatch(
        cardStateAction.restoreCardTaskStateById({
          ...task,
          labwareID: labwareId,
          row: alphabetRow,
          column: numericColumn,
          rowTipCount,
          columnTipCount,
          mixingPosition,
          mixingVolume: validatedVolume,
          volume: validatedVolume,
        }),
      );
      dispatch(globalAddUndoItem());
      dispatch(saveState());
      dispatch(restoreTipCountForSingleCard(selectedCard.ID));
    });
  };

export const saveMixingStateBeforeEdit = () => (dispatch, getState) => {
  const {cardState, labwareState} = getState();

  dispatch(temporaryStateAction.addTemporaryState('mixingStateBeforeEdit', {cardState, labwareState}));
};

export const clearMixingStateBeforeEdit = () => async (dispatch) => {
  dispatch(temporaryStateAction.removeTemporaryState('mixingStateBeforeEdit'));
  dispatch(globalAddUndoItem());
};

export const restoreMixingStateAfterCancelEdit = () => (dispatch, getState) => {
  const {temporaryState} = getState();
  const {cardState, labwareState} = temporaryState.mixingStateBeforeEdit;
  const {cards, tasks} = cardState;

  dispatch(clearMixingStateBeforeEdit());
  dispatch(cardStateAction.restoreCardState(cards));
  dispatch(cardStateAction.restoreCardTaskState(tasks));
  dispatch(labwareStateAction.restoreLabwareState(labwareState));
  dispatch(globalAddUndoItem());
};

export const handlePlateSwapDuplicateInMixing = (labwareID, choosenQuadrant, shiftWell) => (dispatch, getState) => {
  const {cardState} = getState();
  const {cards, tasks} = cardState;

  const mixingCards = filterToArray(cards, {type: CardType.MIXING});
  mixingCards.forEach((card) => {
    const existMixingTasks = filterToArray(tasks, {ID: card.tasks, labwareID});

    if (existMixingTasks.length) {
      choosenQuadrant.forEach((q) => {
        let taskRow = convertRowToNumeric(existMixingTasks[0]?.row);
        let taskColumn = existMixingTasks[0]?.column;
        [taskRow, taskColumn] = shiftWell(taskRow, taskColumn, q);
        dispatch(
          addMixingTask(
            labwareID,
            taskRow,
            taskColumn,
            existMixingTasks[0]?.rowTipCount,
            existMixingTasks[0]?.columnTipCount,
            existMixingTasks[0],
          ),
        );
      });
    }
  });
};

export const updatePipettingProfileValueBusiness = (key, value, id, runtimeMode) => (dispatch) => {
  switch (key) {
    case PipettingParameterFieldKey.MIXING_POSITION:
      dispatch(cardStateAction.setTaskMixingPosition(id, value));
      break;
    case PipettingParameterFieldKey.MIXING_CYCLE:
      dispatch(cardStateAction.setTaskMixingCycle(id, value));
      break;
    case PipettingParameterFieldKey.MIXING_VOLUME:
      dispatch(cardStateAction.setTaskMixingVolume(id, value));
      break;
    case PipettingParameterFieldKey.MIXING_SPEED:
      dispatch(cardStateAction.setTaskMixingSpeed(id, value));
      break;
    case PipettingParameterFieldKey.RETRACT_SPEED:
      dispatch(cardStateAction.setTaskRetractSpeed(id, value));
      break;
    case PipettingParameterFieldKey.MIXING_POSITION_ASPIRATE_AND_DISPENSE:
      dispatch(cardStateAction.setTaskMixingPositionDispense(id, value));
      dispatch(cardStateAction.setTaskMixingPositionAspirate(id, value));
      break;
    case PipettingParameterFieldKey.MIXING_POSITION_ASPIRATE:
      dispatch(cardStateAction.setTaskMixingPositionAspirate(id, value));
      break;
    case PipettingParameterFieldKey.MIXING_POSITION_DISPENSE:
      dispatch(cardStateAction.setTaskMixingPositionDispense(id, value));
      break;
    default:
      break;
  }

  if (!runtimeMode) {
    dispatch(globalAddUndoItem());
    return;
  }

  dispatch(updateRunningProtocol());
};

export const clearHighlightedMixingTask = () => (dispatch, getState) => {
  const {cardState} = getState();
  const {cards} = cardState;

  const selectedCard = find(cards, {isSelected: true});

  if (!selectedCard || selectedCard.type !== CardType.MIXING) {
    return;
  }

  dispatch(taskListBusinessAction.unselectAllTaskInCard(selectedCard));
};
export const addMixingStarterTask = () => (dispatch, getState) => {
  const {cardState} = getState();
  const {cards, tasks} = cardState;

  const [cardId, taskId, number] = generateCardIDAndTaskIDAndNumber(
    cards,
    tasks,
    CardType.MIXING,
    null,
    TaskType.MIXING,
    null,
  );

  const cardName = cards[cardId]?.name || getNewCardName(cards, CardType.MIXING);

  const task = {
    ID: taskId,
    number,
    type: TaskType.MIXING,
    labwareID: undefined,
    volume: 0,
    isIgnored: false,
  };

  batch(() => {
    dispatch(createMixingCard(cardId));
    dispatch(cardStateAction.addCardTask(cardId, cardName, CardType.MIXING, taskId, task));
    dispatch(restoreTipCountForSingleCard(cardId));
  });
};

export const editMixingVolumeTask = (taskID, volume, taskType) => (dispatch) => {
  batch(() => {
    dispatch(cardStateAction.setTaskMixingVolume(taskID, volume));
    dispatch(cardStateAction.setTaskVolume(taskID, volume, taskType));
  });
};
